import {
  Button,
  Container,
  Grid,
  Icon,
  Input,
  Popup,
  Loader,
  Pagination,
} from 'semantic-ui-react';
import {
  NumberParam,
  StringParam,
  useQueryParams,
  ObjectParam,
} from 'use-query-params';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { ContactsTable } from './contacts-table';
import { useCurrentBilikZones } from 'context/current-bilik-zones-context';
import { map, pipe, uniq } from 'remeda';
import { CSVLink } from 'react-csv';
import Content from 'components/content/content';
import ContentHeader from 'components/content-header/content-header';
import ContentHeaderTitle from 'components/content-header-title/content-header-title';
import ContentBody from 'components/content-body/content-body';
import { PhoneNumber } from 'utils';
import { ListContactsData } from 'generated/api-client';
import styled from 'styled-components';
import { useDebounce } from 'use-debounce';
import {
  useListContacts,
  useCountContacts,
} from 'generated/api-client/@tanstack/react-query.gen';

const ContactsListPage: FunctionComponent = () => {
  const { currentBilikZones } = useCurrentBilikZones();

  const currentBilikZoneIds = useMemo(
    () => currentBilikZones?.map((zone) => zone.id),
    [currentBilikZones],
  );

  const itemPerPage = useMemo(() => 50, []);

  const [filters, setFilters] = useQueryParams({
    page: NumberParam,
    proViewStatus: StringParam,
    search: StringParam,
    orderBy: ObjectParam,
  });

  useEffect(() => {
    if (filters.proViewStatus === undefined || filters.proViewStatus === null) {
      setFilters({ proViewStatus: 'published' }, 'replaceIn');
    }
  }, []);

  const [searchInput, setSearchInput] = useState(filters.search ?? '');
  const [debouncedSearch] = useDebounce(searchInput, 300);

  useEffect(() => {
    setFilters({
      search: debouncedSearch || undefined,
      page: undefined,
    });
  }, [debouncedSearch]);

  const contactQuery: ListContactsData['query'] = useMemo(() => {
    const query: ListContactsData['query'] = {};

    if (filters.proViewStatus) {
      query.viewStatus = filters.proViewStatus as
        'draft' | 'published' | 'archived';
    }

    if (filters.search) {
      query.search = filters.search;
    }

    if (filters.page) {
      query.offset = filters.page;
    }

    if (currentBilikZoneIds) {
      query.networkIds = currentBilikZoneIds;
    }

    if (filters.orderBy?.column && filters.orderBy?.direction) {
      if (filters.orderBy.column === 'familyName') {
        query.orderByColumn = filters.orderBy.column;
      }
      query.orderByDirection =
        filters.orderBy.direction === 'descending' ? 'DESC' : 'ASC';
    }

    return query;
  }, [
    currentBilikZoneIds,
    filters?.search,
    filters?.proViewStatus,
    filters?.orderBy?.column,
    filters?.orderBy?.direction,
    filters?.page,
    itemPerPage,
  ]);

  const { data: exportContactsData } = useListContacts({
    query: contactQuery,
  });

  const ContactsCsvData = useMemo(() => {
    if (exportContactsData) {
      const data = pipe(
        exportContactsData.contacts,
        map((contact) => ({
          Nom: `${contact.familyName}`,
          Prénom: `${contact.givenName}`,
          Email: contact.email,
          Téléphone: contact.phoneNumber
            ? PhoneNumber.format(contact.phoneNumber)
            : '',
        })),
        uniq(),
      );

      return data;
    }
  }, [exportContactsData]);

  const ContactsEmailData = useMemo(() => {
    if (exportContactsData) {
      const data = pipe(
        exportContactsData?.contacts,
        map((contact) => contact.email),
        uniq(),
      );

      return data;
    }
  }, [exportContactsData]);

  const { data: counts, isLoading: countsLoading } = useCountContacts({
    query: {
      search: contactQuery.search,
      networkIds: contactQuery.networkIds,
    },
  });

  const totalPages = useMemo<number>(() => {
    if (!counts) return 0;
    const activeCount = filters.proViewStatus
      ? counts[filters.proViewStatus as 'published' | 'draft' | 'archived']
      : counts.total;
    return Math.ceil(activeCount / itemPerPage);
  }, [counts, filters.proViewStatus, itemPerPage]);

  if (currentBilikZones === undefined) {
    return (
      <Content>
        <Loader
          style={{ marginTop: '50px' }}
          size="large"
          active
          inline="centered"
        >
          Chargement...
        </Loader>
      </Content>
    );
  }

  return (
    <Content>
      <ContentHeader>
        <Grid columns={2}>
          <Grid.Column>
            <ContentHeaderTitle>
              Contacts pros
              <small>
                {currentBilikZones
                  ? currentBilikZones.map((zone) => zone.name).join(', ')
                  : 'Toutes les zones'}
              </small>
            </ContentHeaderTitle>
          </Grid.Column>
        </Grid>
      </ContentHeader>
      <ContentBody>
        <Grid>
          <Grid.Column width={14}>
            <Button.Group>
              <Button
                type="button"
                color="grey"
                loading={countsLoading}
                disabled={countsLoading}
                basic={!!filters.proViewStatus}
                onClick={(): void => {
                  setFilters({
                    proViewStatus: undefined,
                    page: undefined,
                  });
                }}
              >
                Tous ({counts?.total})
              </Button>
              <Button
                type="button"
                color="green"
                loading={countsLoading}
                disabled={countsLoading}
                basic={filters.proViewStatus !== 'published'}
                onClick={(): void => {
                  setFilters({
                    proViewStatus: 'published',
                    page: undefined,
                  });
                }}
              >
                Fiches Publiées ({counts?.published})
              </Button>
              <Button
                type="button"
                color="red"
                loading={countsLoading}
                disabled={countsLoading}
                basic={filters.proViewStatus !== 'draft'}
                onClick={(): void => {
                  setFilters({
                    proViewStatus: 'draft',
                    page: undefined,
                  });
                }}
              >
                Fiches Brouillons ({counts?.draft})
              </Button>
              <Button
                type="button"
                color="grey"
                loading={countsLoading}
                disabled={countsLoading}
                basic={filters.proViewStatus !== 'archived'}
                onClick={(): void => {
                  setFilters({
                    proViewStatus: 'archived',
                    page: undefined,
                  });
                }}
              >
                Fiches Archivées ({counts?.archived})
              </Button>
            </Button.Group>
            <StyledInput
              icon="search"
              iconPosition="left"
              placeholder="Contact..."
              value={searchInput}
              onChange={(event): void => setSearchInput(event.target.value)}
            />
          </Grid.Column>
          {exportContactsData ? (
            <Grid.Column width={2} textAlign="right">
              {currentBilikZoneIds ? (
                <Popup
                  position="top right"
                  trigger={
                    <span style={{ paddingTop: 7, paddingBottom: 7 }}>
                      <Button
                        as="a"
                        href={`mailto:?bcc=${ContactsEmailData}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon name="mail" style={{ margin: 0 }} />
                      </Button>
                    </span>
                  }
                  content={`Envoyer un email aux (${exportContactsData?.total}) contacts`}
                />
              ) : null}

              <Popup
                position="top right"
                trigger={
                  <CSVLink
                    className="button ui button"
                    filename={'contacts-pro.csv'}
                    data={ContactsCsvData}
                  >
                    <Icon name="download" style={{ margin: 0 }} />
                  </CSVLink>
                }
                content={`Exporter les (${exportContactsData?.total}) contacts en CSV`}
              />
            </Grid.Column>
          ) : null}
        </Grid>
        <ContactsTable
          queryFilters={{
            ...contactQuery,
            limit: itemPerPage,
            offset: filters.page ? (filters.page - 1) * itemPerPage : 0,
          }}
          onSort={(column, direction): void => {
            setFilters({
              orderBy: { column, direction },
            });
          }}
        />
        {totalPages > 1 ? (
          <Container fluid textAlign="right" style={{ marginTop: '15px' }}>
            <Pagination
              activePage={filters.page ?? 1}
              totalPages={totalPages}
              onPageChange={(event, { activePage }): void => {
                setFilters({
                  page: Number(activePage),
                });
              }}
              ellipsisItem={{
                content: <Icon name="ellipsis horizontal" />,
                icon: true,
              }}
              firstItem={{
                content: <Icon name="angle double left" />,
                icon: true,
              }}
              lastItem={{
                content: <Icon name="angle double right" />,
                icon: true,
              }}
              prevItem={{ content: <Icon name="angle left" />, icon: true }}
              nextItem={{ content: <Icon name="angle right" />, icon: true }}
            />
          </Container>
        ) : null}
      </ContentBody>
    </Content>
  );
};

export default ContactsListPage;

const StyledInput = styled(Input)`
  display: inline-block !important;
  margin-left: 12px;

  input {
    border-radius: 3px !important;
    border-color: #767676 !important;
    padding: 5px !important;

    &:focus {
      border-color: #3c8dbc !important;
    }
  }
`;
